<template>
  <section class="panel" aria-label="License Usage">
    <div class="panel__title">{{ $t('licensing.settings.usage.title') }}</div>
    <div class="panel__usage-bars">
      <div
        v-for="meter in usageMeters"
        :key="meter.key"
        class="usage-row"
      >
        <div class="usage-row__header">
          <span class="usage-row__label">{{ meter.label }}</span>
          <span
            class="usage-row__count"
            :class="getCountClass(meter.percent)"
          >
            {{ meter.display }}
            <v-icon
              v-if="meter.percent >= 100"
              x-small
              color="error"
              class="usage-row__maxed-icon"
            >
              block
            </v-icon>
          </span>
        </div>
        <div
          class="usage-bar"
          role="progressbar"
          :aria-valuenow="meter.percent"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-label="meter.label + ' usage'"
        >
          <div
            class="usage-bar__fill"
            :class="getBarColor(meter.percent)"
            :style="{ width: Math.max(meter.percent, 2) + '%' }"
          />
        </div>
        <div class="usage-bar__footer">
          <div class="usage-bar__scale">
            <span>0</span>
            <span v-if="meter.max">{{ Math.round(meter.max / 2).toLocaleString() }}</span>
            <span>{{ meter.maxLabel }}</span>
          </div>
          <span
            v-if="meter.percent >= 100"
            class="usage-bar__maxed"
          >
            {{ $t('licensing.settings.usage.maxed') }}
          </span>
        </div>
      </div>
      <div
        v-if="usageMeters.length === 0"
        class="usage-row__empty"
      >
        No usage limits on this license
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'LicenseUsagePanel',
  props: {
    permissions: { type: Object, required: true },
    assetCount: { type: Number, default: 0 },
    domainCount: { type: Number, default: 0 },
    keywordCount: { type: Number, default: 0 },
    apiScanCount: { type: Number, default: 0 },
  },
  computed: {
    usageMeters() {
      const meters = [];
      const p = this.permissions;

      // Counts originate from a stats layer that can emit non-numeric values
      // (e.g. {} for empty fields). Coerce defensively so a bad value never
      // renders as a "[object Object]" string instead of a number.
      const assets = this.toCount(this.assetCount);
      const domains = this.toCount(this.domainCount);
      const keywords = this.toCount(this.keywordCount);

      if (p.maxAssets != null) {
        const pct = p.maxAssets ? Math.min(100, Math.round((assets / p.maxAssets) * 100)) : 0;
        meters.push({
          key: 'assets',
          label: this.$t('licensing.settings.usage.assets'),
          current: assets,
          max: p.maxAssets,
          maxLabel: p.maxAssets ? p.maxAssets.toLocaleString() : '∞',
          display: `${assets.toLocaleString()} / ${p.maxAssets ? p.maxAssets.toLocaleString() : 'Unlimited'}`,
          percent: pct,
        });
      }

      if (p.maxDomains) {
        const pct = Math.min(100, Math.round((domains / p.maxDomains) * 100));
        meters.push({
          key: 'domains',
          label: this.$t('licensing.settings.usage.domains'),
          current: domains,
          max: p.maxDomains,
          maxLabel: String(p.maxDomains),
          display: `${domains} / ${p.maxDomains}`,
          percent: pct,
        });
      }

      if (p.keywords) {
        const pct = Math.min(100, Math.round((keywords / p.keywords) * 100));
        meters.push({
          key: 'keywords',
          label: this.$t('licensing.settings.usage.keywords'),
          current: keywords,
          max: p.keywords,
          maxLabel: String(p.keywords),
          display: `${keywords} / ${p.keywords}`,
          percent: pct,
        });
      }

      // API scan bucket meter — only rendered for CYN-API-LIMITED
      // (CYN-API-USAGE has no maxApiScans in scanLimits). Prepaid, lifetime.
      const apiCap = p.scanLimits && p.scanLimits.maxApiScans;
      if (apiCap) {
        const used = this.toCount(this.apiScanCount);
        const pct = Math.min(100, Math.round((used / apiCap) * 100));
        meters.push({
          key: 'apiScans',
          label: this.$t('licensing.settings.usage.api.scans'),
          current: used,
          max: apiCap,
          maxLabel: String(apiCap),
          display: `${used} / ${apiCap}`,
          percent: pct,
        });
      }

      return meters;
    },
  },
  methods: {
    toCount(value) {
      return typeof value === 'number' && Number.isFinite(value) ? value : 0;
    },
    getBarColor(percent) {
      if (percent >= 100) return 'usage-bar__fill--red';
      if (percent >= 90) return 'usage-bar__fill--orange';
      if (percent >= 75) return 'usage-bar__fill--yellow';
      return 'usage-bar__fill--green';
    },
    getCountClass(percent) {
      if (percent >= 100) return 'usage-row__count--red';
      if (percent >= 90) return 'usage-row__count--orange';
      if (percent >= 75) return 'usage-row__count--yellow';
      return '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/_sizing";
@import "@/assets/styles/_colors";
@import "@/assets/styles/_semantic-sizing";

.panel {
  background: var(--bg-secondary);
  border: $border-width-thin solid var(--border-primary);
  border-radius: $radius-lg;
  padding: $space-5 $space-6;

  &__title {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-base;
    margin-bottom: $space-4;
    padding-bottom: $space-2;
    border-bottom: $border-width-thin solid var(--border-primary);
  }
}

.panel__usage-bars {
  display: flex;
  flex-direction: column;
  gap: $space-5;
}

.usage-row {
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: $space-2;
  }

  &__label {
    color: var(--text-primary);
    font-family: $font-family-bold;
    font-size: $font-size-base;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    gap: $space-1;
    color: var(--text-secondary);
    font-family: $font-family-bold;
    font-size: $font-size-base;

    &--yellow { color: $warning-500; }
    &--orange { color: #f97316; }
    &--red { color: $error-500; }
  }

  &__maxed-icon {
    vertical-align: middle;
  }

  &__empty {
    color: var(--text-secondary);
    font-family: $font-family-regular;
    font-size: $font-size-sm;
    text-align: center;
    padding: $space-6 0;
  }
}

.usage-bar {
  height: $space-6;
  background: $gray-200;
  border-radius: $radius-full;
  overflow: hidden;
  position: relative;

  &__fill {
    height: 100%;
    border-radius: $radius-full;
    transition: width 0.4s $transition-timing-ease;

    &--green { background: linear-gradient(90deg, $success-500, lighten($success-500, 10%)); }
    &--yellow { background: linear-gradient(90deg, $warning-500, lighten($warning-500, 10%)); }
    &--orange { background: linear-gradient(90deg, #f97316, #fb923c); }
    &--red { background: linear-gradient(90deg, $error-500, lighten($error-500, 10%)); }
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-top: $space-1-5;
  }

  &__scale {
    display: flex;
    justify-content: space-between;
    flex: 1;
    font-size: $font-size-xs;
    color: var(--text-secondary);
    font-family: $font-family-regular;
    padding: 0 $space-0-5;
  }

  &__maxed {
    font-family: $font-family-bold;
    font-size: $font-size-xs;
    color: $error-500;
    white-space: nowrap;
    margin-left: $space-2;
  }
}
</style>
